import {ChangeDetectionStrategy, Component, effect, inject, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {
  ProductSelectionChange,
  ProductSelectionTableComponent,
} from '../../../common/components/product-selection-table/product-selection-table.component';
import {getProductId} from '../../../models/clare';
import {ProductQueryParams} from '../../../models';
import {CatalogDetailActions} from '../store/catalog-detail.actions';
import {
  selectCatalogProductSelectionError,
  selectCatalogProductSelectionLoading,
  selectCatalogProductSelectionPaginationOptions,
  selectCatalogProductSelectionProducts,
  selectCatalogProductSelectionSaveError,
  selectCatalogProductSelectionSaveStatus,
  selectCatalogProductSelectionSaving,
  selectCatalogProductSelectionSearchQuery,
  selectCatalogProductSelectionSelectedCount,
  selectCatalogProductSelectionSelectedIds,
  selectCatalogProductSelectionSortBy,
  selectCatalogProductSelectionSortDirection,
} from '../store/catalog-detail.selectors';

@Component({
  selector: 'app-choose-catalog-products-modal',
  templateUrl: './choose-catalog-products-modal.component.html',
  styleUrl: './choose-catalog-products-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProductSelectionTableComponent, SeFeAlertModule, SeFeButtonModule, SeFeModalModule],
})
export class ChooseCatalogProductsModalComponent {
  readonly modal = viewChild.required<SeFeModalComponent>('modal');

  private readonly store = inject(Store);

  readonly products = toSignal(this.store.select(selectCatalogProductSelectionProducts), {requireSync: true});
  readonly loading = toSignal(this.store.select(selectCatalogProductSelectionLoading), {requireSync: true});
  readonly error = toSignal(this.store.select(selectCatalogProductSelectionError), {requireSync: true});
  readonly query = toSignal(this.store.select(selectCatalogProductSelectionSearchQuery), {requireSync: true});
  readonly sortBy = toSignal(this.store.select(selectCatalogProductSelectionSortBy), {requireSync: true});
  readonly sortDirection = toSignal(this.store.select(selectCatalogProductSelectionSortDirection), {
    requireSync: true,
  });
  readonly paginationOptions = toSignal(this.store.select(selectCatalogProductSelectionPaginationOptions), {
    requireSync: true,
  });
  readonly selectedProductIds = toSignal(this.store.select(selectCatalogProductSelectionSelectedIds), {
    requireSync: true,
  });
  readonly selectedCount = toSignal(this.store.select(selectCatalogProductSelectionSelectedCount), {
    requireSync: true,
  });
  readonly saving = toSignal(this.store.select(selectCatalogProductSelectionSaving), {requireSync: true});
  readonly saveError = toSignal(this.store.select(selectCatalogProductSelectionSaveError), {requireSync: true});

  private readonly saveStatus = toSignal(this.store.select(selectCatalogProductSelectionSaveStatus), {
    requireSync: true,
  });

  constructor() {
    effect(() => {
      if (this.saveStatus() === 'success') {
        this.modal().close();
      }
    });
  }

  open(focusTarget: EventTarget | null): void {
    this.store.dispatch(CatalogDetailActions.openProductChooser());
    this.modal().open(focusTarget);
  }

  close(): void {
    this.modal().close();
  }

  modalClosed(): void {
    this.store.dispatch(CatalogDetailActions.closeProductChooser());
  }

  queryChanged(queryParams: ProductQueryParams): void {
    this.store.dispatch(CatalogDetailActions.productChoicesQueryChanged({queryParams}));
  }

  selectionChanged({currentPageProductIds, selectedProducts}: ProductSelectionChange): void {
    this.store.dispatch(
      CatalogDetailActions.productChoicesSelectionChanged({
        currentPageProductIds,
        selectedProductIds: selectedProducts.map(product => getProductId(product)).filter((id): id is string => !!id),
      })
    );
  }

  addSelectedProducts(): void {
    this.store.dispatch(CatalogDetailActions.addSelectedProducts());
  }
}
